import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  ArrowLeft,
  Copy,
  Users,
  Sparkles,
  ListPlus,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import api from "../utils/api";
import { toast } from "sonner";
import CountdownTimer from "../components/CountdownTimer";
import ProgressBar from "../components/ProgressBar";
import useAuthStore from "../store/authStore";
import useThemeStore from "../store/themeStore";

const GroupDashboard = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { theme } = useThemeStore();

  const [group, setGroup] = useState(null);
  const [stats, setStats] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [newWishlistItem, setNewWishlistItem] = useState({
    title: "",
    description: "",
    link: "",
    price: "",
  });

  const [address, setAddress] = useState("");
  const [suggestionFilters, setSuggestionFilters] = useState({
    age: "",
    gender: "any",
    interests: "",
    maxPrice: "",
  });
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [groupId]);

  const fetchData = async () => {
    try {
      const [groupRes, statsRes, participantsRes, wishlistRes] =
        await Promise.all([
          api.get(`/groups/${groupId}`),
          api.get(`/groups/${groupId}/stats`),
          api.get(`/groups/${groupId}/participants`),
          api.get(`/wishlist/${user.id}`, { params: { group_id: groupId } }),
        ]);

      setGroup(groupRes.data);
      setStats(statsRes.data);
      setParticipants(participantsRes.data);
      setWishlistItems(wishlistRes.data);
    } catch (error) {
      toast.error(t("error"), {
        description: error.response?.data?.detail || "Failed to load group",
      });
    }
    setLoading(false);
  };

  const formatCurrency = useCallback((value) => {
    if (value === null || value === undefined) {
      return null;
    }
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return null;
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "EUR",
      }).format(numericValue);
    } catch (error) {
      return `${numericValue} €`;
    }
  }, []);

  const handleFetchSuggestions = useCallback(async () => {
    setSuggestionsLoading(true);
    try {
      const params = {};
      const parsedAge = parseInt(suggestionFilters.age, 10);
      if (!Number.isNaN(parsedAge) && parsedAge >= 0) {
        params.age = parsedAge;
      }
      if (suggestionFilters.gender && suggestionFilters.gender !== "any") {
        params.gender = suggestionFilters.gender;
      }
      if (suggestionFilters.interests) {
        params.interests = suggestionFilters.interests;
      }
      const parsedPrice = parseFloat(suggestionFilters.maxPrice);
      if (!Number.isNaN(parsedPrice) && parsedPrice >= 0) {
        params.max_price = parsedPrice;
      }

      const response = await api.get("/affiliate/suggestions", {
        params,
      });
      setSuggestions(response.data);
    } catch (error) {
      toast.error(t("error"), {
        description: error.response?.data?.detail || t("suggestionsLoadFailed"),
      });
    } finally {
      setSuggestionsLoading(false);
    }
  }, [suggestionFilters, t]);

  useEffect(() => {
    handleFetchSuggestions();
  }, []);

  const handleDeleteGroup = async () => {
    try {
      await api.delete(`/groups/${groupId}`);
      toast.success(t("success"), { description: t("groupDeleted") });
      navigate("/");
    } catch (error) {
      toast.error(t("error"), {
        description: error.response?.data?.detail || t("groupDeleteFailed"),
      });
    }
  };

  const handleApplySuggestion = async (suggestion) => {
    try {
      await api.post("/wishlist", {
        group_id: groupId,
        title: suggestion.title,
        description: suggestion.description || null,
        link: suggestion.affiliate_link,
        price:
          suggestion.price !== null && suggestion.price !== undefined
            ? suggestion.price
            : null,
      });
      toast.success(t("success"), {
        description: t("suggestionAdded"),
      });
      fetchData();
    } catch (error) {
      toast.error(t("error"), {
        description: error.response?.data?.detail || t("suggestionAddFailed"),
      });
    }
  };

  const handleCopyInviteLink = () => {
    const inviteLink = `${window.location.origin}/join/${group.invite_code}`;
    navigator.clipboard.writeText(inviteLink);
    toast.success(t("copied"));
  };

  const handlePerformDraw = async () => {
    try {
      await api.post(`/groups/${groupId}/draw`);
      toast.success(t("success"), { description: "Draw completed!" });
      fetchData();
    } catch (error) {
      toast.error(t("error"), {
        description: error.response?.data?.detail || "Failed to perform draw",
      });
    }
  };

  const handleAddWishlistItem = async () => {
    if (!newWishlistItem.title) {
      toast.error(t("error"), { description: "Title is required" });
      return;
    }

    try {
      await api.post("/wishlist", {
        group_id: groupId,
        title: newWishlistItem.title,
        description: newWishlistItem.description || null,
        link: newWishlistItem.link || null,
        price: newWishlistItem.price ? parseFloat(newWishlistItem.price) : null,
      });

      toast.success(t("success"), { description: "Item added!" });
      setNewWishlistItem({ title: "", description: "", link: "", price: "" });
      fetchData();
    } catch (error) {
      toast.error(t("error"), {
        description: error.response?.data?.detail || "Failed to add item",
      });
    }
  };

  const handleDeleteWishlistItem = async (itemId) => {
    try {
      await api.delete(`/wishlist/${itemId}`);
      toast.success(t("success"), { description: "Item deleted!" });
      fetchData();
    } catch (error) {
      toast.error(t("error"), {
        description: error.response?.data?.detail || "Failed to delete item",
      });
    }
  };

  const handleSaveAddress = async () => {
    if (!address) {
      toast.error(t("error"), { description: "Address is required" });
      return;
    }

    try {
      await api.post("/address", { address });
      toast.success(t("success"), { description: "Address saved securely!" });
      fetchData();
    } catch (error) {
      toast.error(t("error"), {
        description: error.response?.data?.detail || "Failed to save address",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">{t("loading")}</div>
      </div>
    );
  }

  if (!group) return null;

  const isAdmin = group.created_by === user?.id;

  return (
    <div
      className={`min-h-screen ${
        theme === "dark"
          ? "bg-gray-900"
          : "bg-gradient-to-br from-red-50 via-pink-50 to-blue-50"
      } py-8 px-4`}
    >
      <div className="container mx-auto max-w-6xl">
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("myGroups")}
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-2 gap-6"
        >
          {/* Header */}
          <div className="p-6 col-span-2">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="text-center mx-auto">
                <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-2">
                  {group.name}
                </h1>
                {group.theme && (
                  <p className="text-gray-600 dark:text-gray-400">
                    {group.theme}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-center mt-4 gap-4">
              {group.draw_completed ? (
                <Button
                  data-testid="view-result-button"
                  onClick={() => navigate(`/group/${groupId}/draw`)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {t("viewResult")}
                </Button>
              ) : (
                isAdmin &&
                stats?.total_participants >= 2 && (
                  <Button
                    data-testid="perform-draw-button"
                    onClick={handlePerformDraw}
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    {t("performDraw")}
                  </Button>
                )
              )}
            </div>
          </div>
          {/* Countdown */}
          <div className="col-span-2 md:col-span-1">
            <CountdownTimer targetDate={group.gift_date} />
          </div>
          {/* Invite Code */}
          <Card className="p-6 col-span-2 md:col-span-1 ">
            <div className="flex flex-col gap-4">
              <div className="flex flex-row items-center gap-4">
                <Label className="text-sm text-gray-600 dark:text-gray-400">
                  {t("inviteCode")}:
                </Label>
                <p className="text-lg  font-mono font-bold text-rose-600">
                  {group.invite_code}
                </p>
              </div>
              <Button
                data-testid="copy-invite-link-button"
                onClick={handleCopyInviteLink}
                variant="outline"
              >
                <Copy className="mr-2 h-4 w-4" />
                {t("copyInviteLink")}
              </Button>
            </div>
          </Card>
          {/* Progress Stats */}
          {stats && (
            <Card className="p-6 col-span-2 md:col-span-1">
              <h3 className="text-xl font-semibold mb-4">
                {t("groupProgress")}
              </h3>
              <div className="space-y-4">
                <ProgressBar
                  label={t("wishlistsAdded")}
                  percentage={stats.wishlist_percentage}
                />
                <ProgressBar
                  label={t("addressesAdded")}
                  percentage={stats.address_percentage}
                />
              </div>
            </Card>
          )}
          {/* Participants */}
          <Card className="col-span-2 md:col-span-1 p-6 ">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t("participants")} ({participants.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {participants.map((participant) => (
                <div
                  key={participant.id}
                  className="flex flex-row items-center p-2 rounded-lg bg-gray-50 dark:bg-gray-800"
                >
                  {/* delete participant from group */}
                  {isAdmin && participant.id !== group.created_by && (
                    <Button
                      data-testid={`delete-participant-${participant.id}`}
                      variant="destructive"
                      size="sm"
                      className="mr-2"
                      onClick={async () => {
                        try {
                          await api.post(
                            `/groups/${groupId}/remove/${participant.id}`
                          );
                          toast.success(t("success"), {
                            description: t("participantRemoved"),
                          });
                          fetchData();
                        } catch (error) {
                          toast.error(t("error"), {
                            description:
                              error.response?.data?.detail ||
                              t("participantRemoveFailed"),
                          });
                        }
                      }}
                    >
                      <Trash2 />
                    </Button>
                  )}
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {participant.name}
                  </div>
                </div>
              ))}
            </div>
          </Card>
          {/* Wishlist Items */}
          <Card className="p-4 col-span-2">
            <h2 className="text-xl font-bold p-2">{t("wishlist")}</h2>
            <div className="space-y-4">
              {wishlistItems.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  {t("noWishlist")}
                </p>
              ) : (
                wishlistItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 flex justify-between items-start"
                  >
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 dark:text-white">
                        {item.title}
                      </h5>
                      {item.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {item.description}
                        </p>
                      )}
                      <div className="flex gap-4 mt-2">
                        {item.link && (
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-rose-600 hover:underline"
                          >
                            {t("link")}
                          </a>
                        )}
                        {item.price && (
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            ${item.price}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      data-testid={`delete-wishlist-item-${item.id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteWishlistItem(item.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </Card>
          {/* Tabs for Gift Suggestions and Address */}
          <Card className="col-span-2 p-6">
            <Tabs defaultValue="wishlist">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger data-testid="wishlist-tab" value="wishlist">
                  {t("myWishlist")}
                </TabsTrigger>
                <TabsTrigger data-testid="address-tab" value="address">
                  {t("myAddress")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="wishlist" className="space-y-6">
                {/* Gift Suggestions */}
                {/* erst einkommentieren, wenn Vorschlagsliste funktioniert */}

                {/* <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-4 space-y-4">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="mt-1 rounded-full bg-rose-100 dark:bg-rose-900/40 p-2 text-rose-600 dark:text-rose-300">
                        <Lightbulb className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {t("giftIdeas")}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t("giftIdeasSubtitle")}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      onClick={handleFetchSuggestions}
                      disabled={suggestionsLoading}
                      variant="outline"
                      className="w-full md:w-auto"
                    >
                      {suggestionsLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t("loadingIdeas")}
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Search className="h-4 w-4" />
                          {t("findGiftIdeas")}
                        </span>
                      )}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <Label htmlFor="suggestion-age">
                        {t("ageFilterLabel")}
                      </Label>
                      <Input
                        id="suggestion-age"
                        type="number"
                        min="0"
                        value={suggestionFilters.age}
                        onChange={(e) =>
                          setSuggestionFilters((prev) => ({
                            ...prev,
                            age: e.target.value,
                          }))
                        }
                        placeholder="12"
                      />
                    </div>
                    <div>
                      <Label>{t("genderFilterLabel")}</Label>
                      <Select
                        value={suggestionFilters.gender}
                        onValueChange={(value) =>
                          setSuggestionFilters((prev) => ({
                            ...prev,
                            gender: value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">{t("genderAny")}</SelectItem>
                          <SelectItem value="female">
                            {t("genderFemale")}
                          </SelectItem>
                          <SelectItem value="male">
                            {t("genderMale")}
                          </SelectItem>
                          <SelectItem value="nonbinary">
                            {t("genderNonBinary")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="suggestion-interests">
                        {t("interestsFilterLabel")}
                      </Label>
                      <Input
                        id="suggestion-interests"
                        value={suggestionFilters.interests}
                        onChange={(e) =>
                          setSuggestionFilters((prev) => ({
                            ...prev,
                            interests: e.target.value,
                          }))
                        }
                        placeholder={t("interestsPlaceholder")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="suggestion-budget">
                        {t("budgetFilterLabel")}
                      </Label>
                      <Input
                        id="suggestion-budget"
                        type="number"
                        min="0"
                        step="0.5"
                        value={suggestionFilters.maxPrice}
                        onChange={(e) =>
                          setSuggestionFilters((prev) => ({
                            ...prev,
                            maxPrice: e.target.value,
                          }))
                        }
                        placeholder="25"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    {suggestionsLoading ? (
                      <div className="flex items-center justify-center py-6 text-sm text-gray-500 dark:text-gray-400">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("loadingIdeas")}
                      </div>
                    ) : suggestions.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t("noSuggestions")}
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {suggestions.map((suggestion) => {
                          const priceLabel = formatCurrency(suggestion.price);
                          return (
                            <Card
                              key={suggestion.id}
                              className="flex flex-col gap-3 p-4"
                            >
                              {suggestion.image_url && (
                                <img
                                  src={suggestion.image_url}
                                  alt={suggestion.title}
                                  className="h-36 w-full rounded-md object-cover"
                                />
                              )}
                              <div className="flex items-start gap-2">
                                <Gift className="mt-1 h-4 w-4 text-rose-500" />
                                <div>
                                  <h5 className="font-semibold text-gray-900 dark:text-white">
                                    {suggestion.title}
                                  </h5>
                                  {suggestion.description && (
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                      {suggestion.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                                {priceLabel && (
                                  <span className="font-semibold text-rose-600 dark:text-rose-300">
                                    {priceLabel}
                                  </span>
                                )}
                                {suggestion.merchant && (
                                  <span>{suggestion.merchant}</span>
                                )}
                              </div>
                              {suggestion.interests?.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {suggestion.interests.map((interest) => (
                                    <Badge key={interest} variant="secondary">
                                      {interest}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                                <Button
                                  className="flex-1"
                                  onClick={() =>
                                    handleApplySuggestion(suggestion)
                                  }
                                >
                                  {t("addToWishlistFromSuggestions")}
                                </Button>
                                <Button
                                  asChild
                                  variant="outline"
                                  className="flex-1"
                                >
                                  <a
                                    href={suggestion.affiliate_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2"
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                    {t("viewOffer")}
                                  </a>
                                </Button>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div> */}

                {/* Add New Item Form */}
                <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-4 space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <ListPlus className="h-5 w-5" />
                    {t("addItem")}
                  </h4>
                  <div className="grid gap-4">
                    <div>
                      <Label>{t("itemName")}</Label>
                      <Input
                        data-testid="wishlist-title-input"
                        value={newWishlistItem.title}
                        onChange={(e) =>
                          setNewWishlistItem({
                            ...newWishlistItem,
                            title: e.target.value,
                          })
                        }
                        placeholder="e.g., Board Game"
                      />
                    </div>
                    <div>
                      <Label>{t("description")}</Label>
                      <Textarea
                        data-testid="wishlist-description-input"
                        value={newWishlistItem.description}
                        onChange={(e) =>
                          setNewWishlistItem({
                            ...newWishlistItem,
                            description: e.target.value,
                          })
                        }
                        placeholder="More details..."
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>{t("link")}</Label>
                        <Input
                          data-testid="wishlist-link-input"
                          value={newWishlistItem.link}
                          onChange={(e) =>
                            setNewWishlistItem({
                              ...newWishlistItem,
                              link: e.target.value,
                            })
                          }
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <Label>{t("price")}</Label>
                        <Input
                          data-testid="wishlist-price-input"
                          type="number"
                          min="0"
                          step="0.01"
                          value={newWishlistItem.price}
                          onChange={(e) =>
                            setNewWishlistItem({
                              ...newWishlistItem,
                              price: e.target.value,
                            })
                          }
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <Button
                      data-testid="add-wishlist-item-button"
                      onClick={handleAddWishlistItem}
                      className="w-full"
                    >
                      {t("addItem")}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="address" className="space-y-4">
                <div>
                  <Label>{t("addressTitle")}</Label>
                  <Textarea
                    data-testid="address-input"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder={t("addressPlaceholder")}
                    rows={4}
                    className="mt-2"
                  />
                </div>
                <Button
                  data-testid="save-address-button"
                  onClick={handleSaveAddress}
                  className="w-full"
                >
                  {t("saveAddress")}
                </Button>
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  🔒 Your address is encrypted and only visible to your Secret
                  Santa
                </p>
              </TabsContent>
            </Tabs>
          </Card>
          {isAdmin ? (
            <Button
              onClick={() => setShowDeleteDialog(true)}
              variant="destructive"
            >
              {t("deleteGroup")}
            </Button>
          ) : null}
        </motion.div>
      </div>
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-group-dialog">
          <DialogHeader>
            <DialogTitle>{t("deleteGroup")}</DialogTitle>
            <DialogDescription>{t("deleteGroupDescription")}</DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex gap-4">
            <Button
              data-testid="confirm-delete-group-button"
              variant="destructive"
              onClick={handleDeleteGroup}
              className="flex-1"
            >
              {t("deleteGroup")}
            </Button>
            <Button
              data-testid="cancel-delete-group-button"
              type="button"
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="flex-1"
            >
              {t("cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// const Participants = ({ participants, groupId, fetchData }) => {
//   return (
//     <Card className="col-span-2 md:col-span-1 p-6 ">
//       <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
//         <Users className="h-5 w-5" />
//         {t("participants")} ({participants.length})
//       </h3>
//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
//         {participants.map((participant) => (
//           <div
//             key={participant.id}
//             className="flex flex-row items-center p-2 rounded-lg bg-gray-50 dark:bg-gray-800"
//           >
//             {/* delete participant from group */}
//             <Button
//               data-testid={`delete-participant-${participant.id}`}
//               variant="ghost"
//               size="sm"
//               onClick={async () => {
//                 try {
//                   await api.delete(
//                     `/groups/${groupId}/participants/${participant.id}`
//                   );
//                   toast.success(t("success"), {
//                     description: t("participantRemoved"),
//                   });
//                   fetchData();
//                 } catch (error) {
//                   toast.error(t("error"), {
//                     description:
//                       error.response?.data?.detail ||
//                       t("participantRemoveFailed"),
//                   });
//                 }
//               }}
//             >
//               <Trash2 />
//             </Button>
//             <div className="font-semibold text-gray-900 dark:text-white">
//               {participant.name}
//             </div>
//           </div>
//         ))}
//       </div>
//     </Card>
//   );
// };

export default GroupDashboard;
